/**
 * InventoryManager - Manages player inventory with 4 slots
 */

import { Axe, Sword, Bow, Gun } from '../controllers/Weapon.js';

export class InventoryManager {
    constructor(player, assetManager) {
        this.player = player;
        this.assetManager = assetManager;
        
        // Inventory slots (0-3)
        this.slots = [null, null, null, null];
        this.activeSlot = 0;
        
        // Reference to currently active weapon
        this.activeWeapon = null;
        
        // Weapon attachment point in player model
        this.attachmentPoint = 'right-hand';
    }
    
    /**
     * Initialize inventory with default weapons for player class
     */
    async init() {
        if (!this.player || !this.assetManager) {
            console.error('Cannot initialize inventory: missing player or asset manager');
            return false;
        }
        
        console.log('Initializing empty inventory - weapons are now attached to character models by default');
        
        try {
            // No longer initializing slots with weapons
            // Slots are kept empty for future items like buffs and powers
            this.slots = [null, null, null, null];
            this.activeSlot = 0;
            
            return true;
        } catch (error) {
            console.error('Error initializing inventory:', error);
            return false;
        }
    }
    
    /**
     * Set the active inventory slot (for future inventory items - not weapons)
     * @param {number} slotIndex - Index of the slot (0-3)
     * @returns {boolean} - Whether the slot change was successful
     */
    setActiveSlot(slotIndex) {
        // Validate slot index
        if (slotIndex < 0 || slotIndex > 3) {
            console.error(`Invalid slot index: ${slotIndex}`);
            return false;
        }
        
        // Update active slot for future inventory items
        this.activeSlot = slotIndex;
        console.log(`Changed to inventory slot ${slotIndex+1}`);
        
        // Note: Weapons are no longer stored in inventory or equipped from here
        // This method is kept for future inventory items like buffs and powers
        
        return true;
    }
    
    /**
     * Equip a weapon on the player model
     * @param {Weapon} weapon - The weapon to equip
     * @returns {boolean} - Whether the equip was successful
     */
    equipWeapon(weapon) {
        if (!weapon || !weapon.modelMesh) {
            console.warn('Cannot equip undefined or unloaded weapon');
            return false;
        }
        
        if (!this.player || !this.player.modelMesh) {
            console.warn('Cannot equip weapon: player model not initialized');
            return false;
        }
        
        // First make sure we remove any existing weapon
        this.unequipCurrentWeapon();
        
        // Attach weapon to player model
        if (this.player.modelMesh && weapon.modelMesh) {
            // Find the hand bone to attach the weapon to
            let handBone = null;
            
            // Common hand bone naming patterns in different 3D models
            const handBoneNames = [
                'mixamorigRightHand', 'Hand_R', 'hand_r', 'RightHand', 'righthand',
                'right_hand', 'hand.R', 'HandR', 'hand.right', 'hand_right'
            ];
            
            // Search for the hand bone in the skeleton
            this.player.modelMesh.traverse(node => {
                if ((node.isBone || node.type === 'Bone') && !handBone) {
                    const lowerName = node.name.toLowerCase();
                    // Check if this node's name matches any of our hand bone patterns
                    if (handBoneNames.some(name => lowerName.includes(name.toLowerCase()) ||
                       (lowerName.includes('right') && lowerName.includes('hand')))) {
                        handBone = node;
                        console.log(`Found hand bone: ${node.name}`);
                    }
                }
            });
            
            // If we found a hand bone, attach the weapon to it
            if (handBone) {
                console.log(`Attaching ${weapon.name} to hand bone: ${handBone.name}`);
                
                // Apply weapon-specific offsets based on its type
                if (weapon.name === 'Axe') {
                    // Hide the inventory weapon but keep track of it
                    weapon.modelMesh.visible = false;
                    
                    // Find and use the built-in 2H_Axe on the character model instead
                    let characterAxe = null;
                    this.player.modelMesh.traverse(node => {
                        // Look for the built-in axe by name
                        if (node.name === '2H_Axe' || node.name.toLowerCase().includes('axe')) {
                            characterAxe = node;
                            console.log(`Found character's built-in axe: ${node.name}`);
                        }
                    });
                    
                    if (characterAxe) {
                        // Make the built-in axe visible
                        characterAxe.visible = true;
                        
                        // Store reference to the character's built-in axe
                        weapon.characterAxe = characterAxe;
                        
                        console.log('Using character\'s built-in axe instead of inventory axe');
                        
                        // Play attach animation
                        this._playAxeAttachAnimation();
                    } else {
                        // Fallback if built-in axe is not found
                        console.warn('Could not find character\'s built-in axe, using inventory axe');
                        weapon.modelMesh.visible = true;
                        
                        // Reset position and rotation
                        weapon.modelMesh.position.set(0, 0, 0);
                        weapon.modelMesh.rotation.set(0, 0, 0);
                        
                        // Apply specific positioning for axe in hand
                        weapon.modelMesh.position.x = 0;
                        weapon.modelMesh.position.y = 0;
                        weapon.modelMesh.position.z = 0.1;
                        
                        // Apply specific rotation for axe in hand
                        weapon.modelMesh.rotation.x = 0;
                        weapon.modelMesh.rotation.y = Math.PI / 2; // Rotate 90 degrees on Y axis
                        weapon.modelMesh.rotation.z = 0;
                        
                        // Apply scale
                        weapon.modelMesh.scale.set(weapon.scale, weapon.scale, weapon.scale);
                    }
                } else if (weapon.name === 'Sword') {
                    // Position and rotate the sword properly in the hand
                    weapon.modelMesh.position.set(0, 0, 0);
                    weapon.modelMesh.rotation.set(0, 0, 0);
                    
                    // Apply sword-specific positioning
                    weapon.modelMesh.position.x = 0;
                    weapon.modelMesh.position.y = -0.15;
                    weapon.modelMesh.position.z = 0.1;
                    
                    // Apply sword-specific rotation
                    weapon.modelMesh.rotation.x = Math.PI / 6; // Slight tilt up
                } else if (weapon.name === 'Bow' || weapon.name === 'Gun') {
                    // Position and rotate ranged weapons properly in the hand
                    weapon.modelMesh.position.set(0, 0, 0);
                    weapon.modelMesh.rotation.set(0, 0, 0);
                    
                    // Apply ranged weapon positioning
                    weapon.modelMesh.position.x = 0.1;
                    weapon.modelMesh.position.y = -0.05;
                    weapon.modelMesh.position.z = 0.15;
                    
                    // Apply ranged weapon rotation
                    weapon.modelMesh.rotation.y = -Math.PI / 2; // Point forward
                }
                
                // Apply the weapon's scale
                if (weapon.name !== 'Axe') {
                    // For other weapons, apply scale here
                    weapon.modelMesh.scale.set(weapon.scale, weapon.scale, weapon.scale);
                }
                
                // Add the weapon to the hand bone
                handBone.add(weapon.modelMesh);
                this.activeWeapon = weapon;
                
                console.log(`Equipped weapon in slot ${this.activeSlot}: ${weapon.name}`);
                return true;
            } else {
                // If no hand bone found, fallback to the old method (attach to player mesh)
                console.warn('No hand bone found, attaching weapon to player model root');
                this.player.modelMesh.add(weapon.modelMesh);
                this.activeWeapon = weapon;
                
                console.log(`Equipped weapon in slot ${this.activeSlot}: ${weapon.name}`);
                return true;
            }
        }
        
        return false;
    }
    
    /**
     * Unequip the current weapon from the player model
     */
    unequipCurrentWeapon() {
        if (this.activeWeapon && this.player.modelMesh) {
            // If this is an axe, check if we were using the character's built-in axe
            if (this.activeWeapon.name === 'Axe' && this.activeWeapon.characterAxe) {
                // Hide the character's built-in axe
                this.activeWeapon.characterAxe.visible = false;
                console.log('Hiding character\'s built-in axe');
            }
            
            // Remove the inventory weapon mesh if it exists
            if (this.activeWeapon.modelMesh && this.player.modelMesh) {
                // Check if the weapon is attached to the player model directly
                const isDirectChild = this.player.modelMesh.children.includes(this.activeWeapon.modelMesh);
                
                if (isDirectChild) {
                    // Remove from player model directly
                    this.player.modelMesh.remove(this.activeWeapon.modelMesh);
                } else {
                    // The weapon might be attached to a bone in the skeleton
                    // Search through all bones to find and remove the weapon
                    let weaponFound = false;
                    
                    this.player.modelMesh.traverse(node => {
                        if (node.children && node.children.includes(this.activeWeapon.modelMesh)) {
                            node.remove(this.activeWeapon.modelMesh);
                            console.log(`Removed weapon from bone: ${node.name}`);
                            weaponFound = true;
                        }
                    });
                    
                    if (!weaponFound) {
                        console.warn('Could not find weapon in player model hierarchy');
                    }
                }
                
                // Check for any other weapon meshes that might be children of the player model
                // and remove them to prevent duplicates
                if (this.player.modelMesh.children) {
                    // First check direct children of player model
                    const weaponMeshes = this.player.modelMesh.children.filter(child => 
                        child.userData && child.userData.type === 'weapon'
                    );
                    
                    weaponMeshes.forEach(mesh => {
                        this.player.modelMesh.remove(mesh);
                    });
                    
                    // Then check all bones for weapon attachments
                    this.player.modelMesh.traverse(node => {
                        if (node.children) {
                            const boneWeaponMeshes = node.children.filter(child => 
                                child.userData && child.userData.type === 'weapon'
                            );
                            
                            boneWeaponMeshes.forEach(mesh => {
                                node.remove(mesh);
                            });
                        }
                    });
                }
            }
            
            this.activeWeapon = null;
        }
    }
    
    /**
     * Get the currently active weapon
     * @returns {Weapon|null} - The active weapon or null if none
     */
    getActiveWeapon() {
        // Weapons are now directly attached to character models
        // This method is kept for compatibility with future inventory items
        return null;
    }
    
    /**
     * Replace a weapon in a specific slot
     * @param {number} slotIndex - The slot to replace (0-3)
     * @param {Weapon} weapon - The new weapon
     * @returns {boolean} - Whether the replacement was successful
     */
    replaceWeapon(slotIndex, weapon) {
        // Validate slot index
        if (slotIndex < 0 || slotIndex > 3) {
            console.error(`Invalid slot index: ${slotIndex}`);
            return false;
        }
        
        // Dispose of old weapon if exists
        if (this.slots[slotIndex]) {
            // If this is the active weapon, unequip it first
            if (this.activeSlot === slotIndex) {
                this.unequipCurrentWeapon();
            }
            
            this.slots[slotIndex].dispose();
        }
        
        // Set new weapon
        this.slots[slotIndex] = weapon;
        
        // Load the weapon
        if (weapon) {
            weapon.load(this.assetManager);
            
            // If this is the active slot, equip the new weapon
            if (this.activeSlot === slotIndex) {
                this.activeWeapon = weapon;
                this.equipWeapon(weapon);
            }
        }
        
        return true;
    }
    
    /**
     * Update the inventory state
     * @param {number} deltaTime - Time since last update
     */
    update(deltaTime) {
        // Update active weapon
        if (this.activeWeapon) {
            this.activeWeapon.update(deltaTime);
        }
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        // Unequip any active weapon
        this.unequipCurrentWeapon();
        
        // Dispose of all weapons
        for (let i = 0; i < this.slots.length; i++) {
            if (this.slots[i]) {
                this.slots[i].dispose();
                this.slots[i] = null;
            }
        }
        
        this.activeWeapon = null;
    }
    
    /**
     * Play an attack/attachment animation for the axe
     * @private
     */
    _playAxeAttachAnimation() {
        // Schedule a delayed animation for the axe to simulate "attaching" it
        if (this.player && this.player.playAnimation && typeof this.player.playAnimation === 'function') {
            // First play a short attack animation
            this.player.playAnimation('attack');
            
            // Then schedule the weapon to appear after a short delay
            setTimeout(() => {
                // Find and make visible any character model axe pieces
                this.player.modelMesh.traverse(node => {
                    if (node.name === '2H_Axe' || node.name.toLowerCase().includes('axe')) {
                        if (!node.visible) {
                            console.log(`Making axe part visible: ${node.name}`);
                            node.visible = true;
                        }
                    }
                });
            }, 300); // 300ms delay matches typical attack animation timing
        }
    }
} 